import { useState, type ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { CountrySelect } from "./CountrySelect";
import { EditableCell } from "./EditableCell";
import { LanguageMultiSelect } from "./LanguageMultiSelect";
import { RatingSelect } from "./RatingSelect";
import { StatusSelect } from "./StatusSelect";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface BasicInfoRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | null,
  ) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Single row in the Basic Info view table
 * Shows: Title, Year, Country, Languages, Rating, Runtime, Genres, Status
 */
export function BasicInfoRow({
  title,
  isSelected,
  onSelect,
  onFieldUpdate,
  onDelete,
  onDuplicate,
}: Readonly<BasicInfoRowProps>): ReactElement {
  const [isTogglingPublish, setIsTogglingPublish] = useState(false);

  const handlePublishToggle = async (published: boolean) => {
    setIsTogglingPublish(true);
    try {
      await onFieldUpdate(title.id, "published", published);
    } finally {
      setIsTogglingPublish(false);
    }
  };

  const handleCountryChange = (country: string | null) => {
    void onFieldUpdate(title.id, "country", country);
  };

  const handleLanguagesChange = (languages: string[]) => {
    void onFieldUpdate(title.id, "languages", languages);
  };

  const handleRatingChange = (rating: string | null) => {
    void onFieldUpdate(title.id, "rating", rating);
  };

  return (
    <TableRow className="hover:bg-neutral-900/30">
      <TableCell className="w-12">
        <Checkbox checked={isSelected} onCheckedChange={(checked) => onSelect(title.id, checked === true)} />
      </TableCell>
      <TableCell>
        <EditableCell
          className="flex flex-col -ml-2 gap-1 text-sm font-medium text-white truncate"
          value={title.name}
          onSave={(v) => onFieldUpdate(title.id, "name", v)}
          type="text"
        />
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.year ?? ""}
          onSave={(v) => onFieldUpdate(title.id, "year", v ? Number(v) : null)}
          type="number"
          className="text-xs"
          inputClassName="text-xs"
        />
      </TableCell>
      <TableCell>
        <CountrySelect value={title.country} onChange={handleCountryChange} />
      </TableCell>
      <TableCell>
        <LanguageMultiSelect value={title.languages} onChange={handleLanguagesChange} />
      </TableCell>
      <TableCell>
        <RatingSelect value={title.rating} onChange={handleRatingChange} />
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.runtimeMinutes ?? ""}
          onSave={(v) => onFieldUpdate(title.id, "runtimeMinutes", v ? Number(v) : null)}
          type="number"
          className="text-xs"
          inputClassName="text-xs"
        />
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.genres.join(", ")}
          onSave={(v) => {
            // Convert comma-separated string to array, trim whitespace, filter empty
            const genresArray =
              typeof v === "string"
                ? v
                    .split(",")
                    .map((g) => g.trim())
                    .filter((g) => g.length > 0)
                : [];
            return onFieldUpdate(title.id, "genres", genresArray);
          }}
          type="text"
          className="text-xs truncate"
          inputClassName="text-xs"
        />
      </TableCell>
      <TableCell>
        <StatusSelect
          value={title.published}
          onChange={(v) => {
            void handlePublishToggle(v);
          }}
          disabled={isTogglingPublish}
        />
      </TableCell>
      <TableCell className="text-center">
        <TitleActionsDropdown titleId={title.id} titleName={title.name} onDelete={onDelete} onDuplicate={onDuplicate} />
      </TableCell>
    </TableRow>
  );
}
